import React, { useState } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

const features = [
  { icon: '🧬', text: 'Biometric fingerprint check-in' },
  { icon: '📊', text: 'Real-time attendance tracking' },
  { icon: '💳', text: 'Payment & membership management' },
  { icon: '🎂', text: 'Birthday & anniversary alerts' },
];

const Login = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await login(values.email, values.password);
    } catch {
      message.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Global reset so nothing causes overflow ── */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body, html, #root { height: 100%; overflow: hidden; }

        .login-root {
          display: flex;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        /* ─── LEFT PANEL ─── */
        .login-left {
          flex: 0 0 48%;
          width: 48%;
          min-height: 100vh;
          background: #001529;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }
        .blob { position: absolute; border-radius: 50%; pointer-events: none; }
        .blob-1 { width: 340px; height: 340px; background: rgba(24,144,255,0.08); top: -90px; right: -90px; }
        .blob-2 { width: 240px; height: 240px; background: rgba(24,144,255,0.06); bottom: -50px; left: -70px; }
        .blob-3 { width: 140px; height: 140px; background: rgba(24,144,255,0.07); bottom: 38%; right: 24px; }

        .left-content {
          position: relative;
          z-index: 2;
          width: 76%;
          max-width: 340px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0;
        }
        .logo-chip {
          width: 58px; height: 58px;
          border-radius: 15px;
          background: rgba(24,144,255,0.15);
          border: 1.5px solid rgba(24,144,255,0.35);
          display: flex; align-items: center; justify-content: center;
          font-size: 26px;
          margin-bottom: 26px;
          backdrop-filter: blur(6px);
          flex-shrink: 0;
        }
        .brand-title {
          color: #fff !important;
          font-size: 42px !important;
          font-weight: 800 !important;
          line-height: 1.13 !important;
          letter-spacing: -0.8px !important;
          margin-bottom: 14px !important;
          white-space: nowrap !important;
          text-align: center !important;
        }
        .brand-tagline {
          color: rgba(255,255,255,0.72);
          font-size: 13.5px;
          line-height: 1.72;
          display: block;
          margin-bottom: 28px;
          text-align: center;
        }
        .feature-list {
          display: flex;
          flex-direction: column;
          gap: 9px;
          width: 100%;
          margin-bottom: 36px;
        }
        .feature-item {
          display: flex;
          align-items: center;
          gap: 11px;
          background: rgba(24,144,255,0.10);
          border: 1px solid rgba(24,144,255,0.22);
          border-radius: 11px;
          padding: 11px 16px;
          white-space: nowrap;
        }
        .feature-item span.fi { font-size: 16px; flex-shrink: 0; line-height: 1; }
        .feature-item span.ft {
          color: rgba(255,255,255,0.90);
          font-size: 13px;
          font-weight: 500;
        }
        .version-tag { color: rgba(255,255,255,0.38); font-size: 11px; }

        /* ─── RIGHT PANEL ─── */
        .login-right {
          flex: 1;
          min-height: 100vh;
          background: #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 24px;
          overflow-y: auto;
        }
        .login-card {
          width: 100%;
          max-width: 400px;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.10);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .card-accent {
          height: 5px;
          background: linear-gradient(90deg, #001529, #1890ff, #096dd9);
        }
        .card-body { padding: 34px 36px 28px; }

        .card-head { text-align: center; margin-bottom: 22px; }
        .head-avatar { font-size: 34px; display: block; margin-bottom: 8px; }
        .card-title {
          color: #1e293b !important;
          font-size: 21px !important;
          font-weight: 700 !important;
          margin: 0 0 5px 0 !important;
        }
        .card-sub { color: #94a3b8; font-size: 13px; display: block; }

        .form-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
          margin: 0 0 22px 0;
        }

        .field-label { color: #475569; font-weight: 600; font-size: 12.5px; letter-spacing: 0.2px; }

        .login-input .ant-input,
        .login-input .ant-input-affix-wrapper {
          border-radius: 10px !important;
          border: 1.5px solid #e2e8f0 !important;
          background: #f8fafc !important;
          font-size: 13.5px !important;
          height: 44px !important;
          transition: border-color .2s !important;
        }
        .login-input .ant-input-affix-wrapper {
          height: 44px !important;
          padding: 0 11px !important;
          align-items: center !important;
        }
        .login-input .ant-input-affix-wrapper:hover,
        .login-input .ant-input-affix-wrapper:focus-within {
          border-color: #1890ff !important;
          box-shadow: 0 0 0 3px rgba(24,144,255,0.12) !important;
        }

        .sign-in-btn {
          width: 100% !important;
          height: 46px !important;
          border-radius: 11px !important;
          background: linear-gradient(135deg, #001529 0%, #1890ff 100%) !important;
          border: none !important;
          font-size: 14.5px !important;
          font-weight: 700 !important;
          letter-spacing: 0.3px !important;
          box-shadow: 0 6px 20px rgba(24,144,255,0.35) !important;
        }
        .sign-in-btn:hover { opacity: 0.92 !important; transform: translateY(-1px) !important; }

        .card-footer {
          display: flex; align-items: center; justify-content: center;
          gap: 7px; margin-top: 22px;
        }
        .footer-dot { width: 3px; height: 3px; border-radius: 50%; background: #cbd5e1; flex-shrink: 0; }
        .footer-txt { color: #94a3b8; font-size: 11px; }

        /* ─── Responsive ─── */
        @media (max-width: 768px) {
          body, html, #root { overflow: auto; }
          .login-root { flex-direction: column; height: auto; min-height: 100vh; }
          .login-left { flex: none; width: 100%; min-height: auto; padding: 48px 0; }
          .left-content { width: 88%; max-width: 100%; white-space: normal; }
          .feature-item { white-space: normal; }
          .login-right { flex: none; width: 100%; min-height: auto; padding: 28px 20px 40px; }
        }
      `}</style>

      <div className="login-root">

        {/* ═══ LEFT ═══ */}
        <div className="login-left">
          <div className="blob blob-1" />
          <div className="blob blob-2" />
          <div className="blob blob-3" />

          <div className="left-content">
            <div className="logo-chip">🏋️</div>

            <Title className="brand-title">
              Wellness Admin
            </Title>

            <Text className="brand-tagline">
              Your all-in-one platform to manage wellness
              centers, track attendance, and empower your team.
            </Text>

            <div className="feature-list">
              {features.map((f, i) => (
                <div key={i} className="feature-item">
                  <span className="fi">{f.icon}</span>
                  <span className="ft">{f.text}</span>
                </div>
              ))}
            </div>

            <span className="version-tag">v2.0 · Wellness Platform</span>
          </div>
        </div>

        {/* ═══ RIGHT ═══ */}
        <div className="login-right">
          <div className="login-card">
            <div className="card-accent" />
            <div className="card-body">

              <div className="card-head">
                <span className="head-avatar">👤</span>
                <Title level={3} className="card-title">Welcome back!</Title>
                <Text className="card-sub">Sign in to manage your wellness center</Text>
              </div>

              <div className="form-divider" />

              <Form
                name="login"
                onFinish={onFinish}
                autoComplete="off"
                layout="vertical"
                requiredMark={false}
              >
                <Form.Item
                  name="email"
                  label={<span className="field-label">Email Address</span>}
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                  style={{ marginBottom: 16 }}
                >
                  <Input
                    className="login-input"
                    size="large"
                    prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<span className="field-label">Password</span>}
                  rules={[{ required: true, message: 'Password is required' }]}
                  style={{ marginBottom: 24 }}
                >
                  <Input.Password
                    className="login-input"
                    size="large"
                    prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="sign-in-btn"
                  >
                    {!loading && '🔐  '}Sign In
                  </Button>
                </Form.Item>
              </Form>

              <div className="card-footer">
                <div className="footer-dot" />
                <span className="footer-txt">
                  © {new Date().getFullYear()} Wellness Admin · All rights reserved
                </span>
                <div className="footer-dot" />
              </div>

            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default Login;
